import { getDecimalResolution } from "@onshopfront/components";
import ArrayPool from "../../Generic/Pools/ArrayPool";
import ObjectPool from "../../Generic/Pools/ObjectPool";
import { PromotionManager } from "./PromotionManager";

export interface KnapsackObject<IdentifierType = string> {
    prices: Array<number>;
    quantities: Array<number>;
    identifiers: Array<IdentifierType>;
    maxQuantities: Array<number>;
}

interface KnapsackSolution<IdentifierType = string> {
    solution: number;
    used: Array<IdentifierType>;
    scale: number;
    usedQuantity: number;
    usedScale: Array<number>;
}

export class KnapsackNoSolutionFoundError extends Error {
    constructor() {
        super("No Knapsack Solution Found");
    }
}

export class Knapsack {
    /**
     * Execute the knapsack algorithm using a greedy solution, it's exceptionally performant, but can get the wrong
     * results
     */
    public static greedy<IdentifierType>(
        possibilities: KnapsackObject<IdentifierType>,
        capacity: number,
        minimumValue: boolean
    ): KnapsackSolution<IdentifierType> {
        let currentIndex = 0;
        const sortedData = [];

        for(let i = 0, l = possibilities.identifiers.length; i < l; i++) {
            sortedData.push({
                ratio     : possibilities.prices[i] / possibilities.quantities[i],
                size      : possibilities.quantities[i],
                identifier: possibilities.identifiers[i],
                remaining : possibilities.maxQuantities[i],
            });
        }

        sortedData.sort((a, b) => {
            if(minimumValue) {
                return a.ratio - b.ratio;
            }

            return b.ratio - a.ratio;
        });

        const solution: KnapsackSolution<IdentifierType> = {
            solution    : 0,
            used        : [],
            scale       : 1,
            usedQuantity: 0,
            usedScale   : [],
        };

        while(solution.usedQuantity < capacity) {
            let matchFound = false;

            for(let i = currentIndex, l = sortedData.length; i < l; i++) {
                if(sortedData[i].remaining <= 0) {
                    currentIndex++;
                    continue;
                }

                for(let j = 0, n = sortedData[i].remaining; j < n; j++) {
                    const sizeLeftInKnapsack = capacity - solution.usedQuantity;

                    if(sortedData[i].size > sizeLeftInKnapsack) {
                        currentIndex++;
                        break;
                    }

                    solution.solution += sortedData[i].ratio * sortedData[i].size;
                    solution.usedQuantity += sortedData[i].size;
                    solution.used.push(sortedData[i].identifier);
                    solution.usedScale.push(1);
                    matchFound = true;
                    currentIndex++;
                    sortedData[i].remaining--;

                    if(solution.usedQuantity >= capacity) {
                        break;
                    }
                }

            }

            if(!matchFound) {
                break;
            }
        }

        PromotionManager.onGreedyCalculationUsed();

        return solution;
    }

    /**
     * Scale the data in preparation for use in the knapsack algorithm
     */
    private static prepareScaledData<IdentifierType>(
        possibilities: KnapsackObject<IdentifierType>,
        capacity: number,
        maxDecimalPlaces: number
    ): {
        scaledCapacity: number;
        scaledValues: Array<number>;
        scaledWeights: Array<number>;
        scale: number;
    } {
        const values = ArrayPool.Get<number>();
        const weights = ArrayPool.Get<number>();
        let scale = 1;

        // Calculate decimal places for scaling
        let decimalPlaces = getDecimalResolution(capacity);

        for(let i = 0, l = possibilities.quantities.length; i < l; i++) {
            decimalPlaces = Math.max(decimalPlaces, getDecimalResolution(possibilities.quantities[i]));

            weights.push(possibilities.quantities[i]);
            values.push(possibilities.prices[i]);
        }

        if(decimalPlaces >= 1) {
            const decimals = Math.min(decimalPlaces, maxDecimalPlaces);

            if(decimals > 0) {
                scale = Math.pow(10, decimals);
            }
        }

        // Apply scaling if needed
        if(scale > 1) {
            capacity = Math.round(capacity * scale);

            for(let i = 0; i < values.length; i++) {
                values[i] *= scale;
                weights[i] *= scale;
            }
        }

        return {
            scaledCapacity: capacity,
            scaledValues  : values,
            scaledWeights : weights,
            scale,
        };
    }

    /**
     * Perform the Knapsack calculation and mutate the provided knapsack array
     * This is a completely rewritten version that properly handles maximum quantities
     */
    private static calculate<TIdentifier extends string | number>(
        values: Array<number>,
        weights: Array<number>,
        maxQuantities: Array<number>,
        identifiers: Array<TIdentifier>,
        capacity: number,
        minimumValue: boolean
    ): { solution: number; items: Record<TIdentifier, number>; } {
        const n = values.length;

        // Value tracking DP
        const dp = Array.from<number>({ length: capacity + 1 }).fill(minimumValue ? Infinity : -Infinity);

        dp[0] = 0;

        // Item tracking DP (stores count used of each identifier)
        const itemUsage = Array.from({ length: capacity + 1 })
            .fill(null)
            .map(() => ({} as Record<TIdentifier, number>));

        itemUsage[0] = {} as Record<TIdentifier, number>;

        for(let i = 0; i < n; i++) {
            let k = maxQuantities[i];
            let count = 1;

            if(k === Infinity) {
                k = 1000000000;
            }

            // Binary expansion to break bounded items into 0/1-like parts
            while(k > 0) {
                const use = Math.min(count, k);
                const itemWeight = weights[i] * use;
                const itemValue = values[i] * use;
                const id = identifiers[i];

                if(!minimumValue) {
                    for(let w = capacity; w >= itemWeight; w--) {
                        const prev = dp[w - itemWeight];

                        if(prev !== -Infinity) {
                            const candidate = prev + itemValue;

                            if(candidate > dp[w]) {
                                dp[w] = candidate;
                                itemUsage[w] = {
                                    ...itemUsage[w - itemWeight],
                                };

                                itemUsage[w][id] = (itemUsage[w][id] || 0) + use;
                            }
                        }
                    }
                } else {
                    for(let w = capacity; w >= itemWeight; w--) {
                        const prev = dp[w - itemWeight];

                        if(prev !== Infinity) {
                            const candidate = prev + itemValue;

                            if(candidate < dp[w]) {
                                dp[w] = candidate;
                                itemUsage[w] = {
                                    ...itemUsage[w - itemWeight],
                                };

                                itemUsage[w][id] = (itemUsage[w][id] || 0) + use;
                            }
                        }
                    }
                }

                k -= use;
                count *= 2;
            }
        }

        const result = dp[capacity];

        ArrayPool.Release(dp);

        if((!minimumValue && result === -Infinity) || (minimumValue && result === Infinity)) {
            throw new KnapsackNoSolutionFoundError();
        }

        return {
            solution: result,
            items   : itemUsage[capacity],
        };
    }

    /**
     * Use the unbound knapsack algorithm to return the best price combination for a product
     * @param possibilities An object that contains the prices, quantities and their identifiers used to
     * determine the best combination
     * @param capacity The maximum capacity (total quantity) the product can have
     * @param minimumValue Whether we should be searching for the minimum value (modified)
     * or the maximum value (original)
     * @returns An object containing the value of the best solution and the identifiers that make up that solution
     */
    public static solve<IdentifierType extends string | number = string>(
        possibilities: KnapsackObject<IdentifierType>,
        capacity: number,
        minimumValue = true
    ): KnapsackSolution<IdentifierType> {
        // This algorithm uses dynamic programming to find the correct value,
        // previously it used brute force but was much too slow
        const capacityThreshold = 100000;

        // Greedy also iterates roughly once per unit of capacity, so beyond this it's too slow and can exceed
        // the max array length; bail out so callers fall back to a flat, non-promotional price instead.
        const massiveCapacityThreshold = 2000000;
        const precisionMultiplier = 1000000;
        const maxDecimalPlaces = 4;

        if(capacity >= massiveCapacityThreshold) {
            throw new KnapsackNoSolutionFoundError();
        }

        // When the capacity is a huge number, we've decided to revert back to using the greedy algorithm
        if(capacity >= capacityThreshold) {
            return Knapsack.greedy(possibilities, capacity, minimumValue);
        }

        // Initialise variables
        const quantityMap = ObjectPool.Get<Record<IdentifierType, number>>();
        const asNumberMap = ObjectPool.Get<Record<IdentifierType, boolean>>();

        // Map the quantities to the identifiers
        for(let i = 0, l = possibilities.identifiers.length; i < l; i++) {
            quantityMap[possibilities.identifiers[i]] = possibilities.quantities[i];
            asNumberMap[possibilities.identifiers[i]] = typeof possibilities.identifiers[i] === "number";
        }

        // Break the possibilities into values and weights
        const {
            scaledCapacity,
            scaledValues,
            scaledWeights,
            scale,
        } = this.prepareScaledData(
            possibilities,
            capacity,
            maxDecimalPlaces
        );

        const identifiers = [ ...possibilities.identifiers ];
        const maxQuantities = [ ...possibilities.maxQuantities ];

        if(scale > 1) {
            let bestSingle = Infinity;
            let bestSingleIndex = -1;

            for(let i = 0, l = scaledValues.length; i < l; i++) {
                if(scaledWeights[i] === scale) {
                    if(minimumValue) {
                        if(bestSingle > scaledValues[i]) {
                            bestSingle = scaledValues[i];
                            bestSingleIndex = i;
                        }
                    } else if(bestSingle < scaledValues[i]) {
                        bestSingle = scaledValues[i];
                        bestSingleIndex = i;
                    }
                }
            }

            if(bestSingleIndex === -1) {
                bestSingleIndex = 0;
            }

            scaledValues.push(scaledValues[bestSingleIndex] / scaledWeights[bestSingleIndex]);
            scaledWeights.push(1);
            maxQuantities.push(Infinity);
            identifiers.push(`scale:${identifiers[bestSingleIndex]}` as IdentifierType);
        }

        // Perform the calculation
        const solution = this.calculate(
            scaledValues,
            scaledWeights,
            maxQuantities,
            identifiers,
            scaledCapacity,
            minimumValue
        );

        // Map the items used into the expected format
        const used: Array<IdentifierType> = [];
        const usedScale: Array<number> = [];
        let quantityUsed = 0;

        for(let identifier in solution.items) {
            if(!Object.hasOwn(solution.items, identifier)) continue;

            let scaleOfAmount = 1;
            const originalIdentifier = identifier;

            if(identifier.startsWith("scale:")) {
                identifier = identifier.slice(6) as Extract<IdentifierType, string>;

                scaleOfAmount = scale;
            }

            const identifierToUse = asNumberMap[identifier] ?
                parseInt(identifier, 10) :
                identifier;

            for(let i = 0; i < solution.items[originalIdentifier]; i++) {
                used.push(identifierToUse as IdentifierType);
                usedScale.push(scaleOfAmount);
                quantityUsed += quantityMap[identifier] / scaleOfAmount;
            }
        }

        const solutionValue = solution.solution;

        // Release any objects we can
        ArrayPool.Release(scaledValues);
        ArrayPool.Release(scaledWeights);
        ObjectPool.Release(solution);
        ObjectPool.Release(quantityMap);
        ObjectPool.Release(asNumberMap);

        // Return the calculated solution
        return {
            solution    : solutionValue,
            usedQuantity: Math.round(quantityUsed * precisionMultiplier) / precisionMultiplier,
            scale,
            used,
            usedScale,
        };
    }

    /**
     * Get the knapsack object using the pool
     */
    public static getObject<IdentifierType = string>(): KnapsackObject<IdentifierType> {
        const possibilities = ObjectPool.Get() as KnapsackObject<IdentifierType>;

        possibilities.prices = ArrayPool.Get();
        possibilities.quantities = ArrayPool.Get();
        possibilities.identifiers = ArrayPool.Get<IdentifierType>();
        possibilities.maxQuantities = ArrayPool.Get();

        return possibilities;
    }

    /**
     * Create an empty result object
     */
    public static getEmptyResult<IdentifierType extends string | number = string>(): KnapsackSolution<IdentifierType> {
        return {
            solution    : 0,
            usedQuantity: 0,
            scale       : 1,
            used        : [],
            usedScale   : [],
        };
    }
}
